import { motion, AnimatePresence } from "framer-motion";
import { Heart, Feather } from "lucide-react";

/** The new 1-paragraph reflections are short enough to display in full. */
const trimToShort = (text) => (text ? text.trim() : text);

/**
 * Quiet-mode staggered reveal:
 *   - Verse fades in first
 *   - Citation follows
 *   - Reflection arrives last
 */
export const VerseCard = ({ verse, isFavorited, onToggleFavorite, onOpenJournal }) => {
  const reflection = trimToShort(verse?.explanation);
  return (
    <div
      className="flex-1 flex items-center justify-center w-full px-8 md:px-16 relative z-10"
      data-testid="verse-card"
    >
      <AnimatePresence mode="wait">
        {verse && (
          <motion.div
            key={verse.reference + verse.translation}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.5 } }}
            className="max-w-2xl mx-auto text-center relative"
          >
            {/* Scripture */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
              className="font-serif-verse text-[#05030A] text-2xl md:text-4xl lg:text-5xl leading-[1.28] font-semibold"
              data-testid="verse-text"
              style={{
                textShadow: "0 1px 2px rgba(247, 244, 239, 0.7), 0 0 8px rgba(247, 244, 239, 0.45)",
              }}
            >
              <span className="text-[#4A5D4E]/70 text-3xl md:text-5xl font-serif-verse align-top mr-1">
                “
              </span>
              {verse.text}
              <span className="text-[#4A5D4E]/70 text-3xl md:text-5xl font-serif-verse align-top ml-1">
                ”
              </span>
            </motion.p>

            {/* Citation */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1], delay: 1.2 }}
              className="mt-8 flex items-center justify-center gap-3"
            >
              <span className="h-px w-10 bg-[#96918A]/40" />
              <p
                className="font-serif-verse italic text-xl md:text-2xl text-[#05030A] tracking-wide font-bold"
                data-testid="verse-reference"
                style={{
                  textShadow:
                    "0 0 10px rgba(255,250,235,0.95), 0 0 20px rgba(255,250,235,0.8)",
                }}
              >
                {verse.reference}
                <span className="font-ui not-italic text-[10px] md:text-xs text-[#96918A] uppercase tracking-[0.2em] ml-2">
                  {verse.translation}
                </span>
              </p>
              <span className="h-px w-10 bg-[#96918A]/40" />
            </motion.div>

            {/* Reflection — luminous backdrop behind text */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1], delay: 2.2 }}
              className="mt-8 max-w-lg mx-auto relative"
              data-testid="verse-explanation"
            >
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: "-14px -10px",
                  zIndex: -1,
                  background:
                    "radial-gradient(60% 80% at 50% 50%, rgba(255,250,235,0.96) 0%, rgba(255,250,235,0.85) 35%, rgba(255,250,235,0.55) 65%, rgba(255,250,235,0) 100%)",
                  filter: "blur(6px)",
                  pointerEvents: "none",
                }}
              />
              <p
                className="font-garamond text-xl md:text-2xl leading-relaxed text-[#05030A] font-medium"
                style={{
                  textShadow:
                    "0 0 14px rgba(255,250,235,1), 0 0 28px rgba(255,250,235,0.9), 0 0 46px rgba(255,250,235,0.7), 0 1px 0 rgba(255,250,235,0.95)",
                }}
              >
                {reflection}
              </p>
            </motion.div>

            {/* Heart + pencil — appear with the reflection */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 2.6 }}
              className="mt-8 flex items-center justify-center gap-3"
            >
              <motion.button
                whileTap={{ scale: 0.85 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite?.(verse);
                }}
                aria-label={isFavorited ? "Remove from favourites" : "Save to favourites"}
                aria-pressed={isFavorited}
                data-testid="favorite-toggle"
                className="ld-icon-chip"
              >
                <Heart
                  className="w-[24px] h-[24px] transition-all"
                  strokeWidth={1.8}
                  fill={isFavorited ? "#1B3478" : "transparent"}
                  color={isFavorited ? "#1B3478" : "#221A10"}
                />
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.85 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenJournal?.(verse);
                }}
                aria-label="Write a reflection"
                data-testid="journal-open"
                className="ld-icon-chip"
              >
                <Feather className="w-[24px] h-[24px]" strokeWidth={1.8} />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
